import React, { useEffect } from 'react';
import { Box, Center, Flex, Text, useToast, FormControl, FormLabel, Select } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BedsoreSelection, {
  BedSoreSelections,
} from '../../components/Resident/BedsoreSelection/BedsoreSelection.tsx';
//import AleaTimeInterval from '../../components/AleaTimeInterval/AleaTimeInterval.tsx';
import AleaIndividualPositions from '../../components/AleaIndividualPositions/AleaIndividualPositions.tsx';
import AleaToggleInline from '../../components/AleaToggle/AleaToggleInline.tsx';
import { IIndividualPosition } from '../../interfaces';
import ResidentService from '../../services/ResidentService.ts';
import bodyRegions from '../../components/Resident/BedsoreSelection/region_mapping.json';
import ResidentHeadline from './ResidentHeadline';
//import { BLACK_COLOR, GRAY_COLOR } from '../../constants/colors.ts';
// import {
//   overviewSectionContainerStyles,
//   overviewSectionTitleContainerStyles,
//   overviewSectionTitleStyles,
// } from '../../styles/ts/overviewStyles.ts';

interface BedsoresPreventionFormValues {
  selections: {
    [key: string]: {
      risk: string;
      note: string;
    };
  };
  dayTime: number;
  nightTime: number;
  positions: IIndividualPosition[];

  enableReposIntervals: boolean;
  enableReposPositionIntervals: boolean;
  enableReposBedsores: boolean;
  bedsoreOps: Array<{ action: 'create' | 'update' | 'delete'; key: string; risk?: string; note?: string }>;
}

// Type for time interval
interface TimeInterval {
  start: string;
  end: string;
  id: string;
  isExisting: boolean;
  dbId: string | null;
  intervalValue: string; // Individual interval duration for each interval
}

interface BedsoresPreventionTabProps {
  gender: 'male' | 'female' | 'diverse';
  residentId: string | undefined;
  initialPositions?: IIndividualPosition[];
  setInitialPositions: (positions: IIndividualPosition[]) => void;
  timeIntervals: TimeInterval[];
  setTimeIntervals: (intervals: TimeInterval[]) => void;
  originalIntervals: TimeInterval[];
  setOriginalIntervals: (intervals: TimeInterval[]) => void;
}

const BedsoresPreventionTab: React.FC<BedsoresPreventionTabProps> = ({
  gender,
  residentId,
  timeIntervals,
  setTimeIntervals,
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<BedsoresPreventionFormValues>();
  const positions = watch('positions') || [];
  //const nightTime = watch('nightTime') || 3;
  const enableReposIntervals = watch('enableReposIntervals') || false;
  const enableReposPositionIntervals = watch('enableReposPositionIntervals') || false;
  const enableReposBedsores = watch('enableReposBedsores') || false;
  const [selections, setSelections] = React.useState<BedSoreSelections>({});
  const toast = useToast();
  // Default positions to show when enabling position-dependent repositioning in create flow
  // Use the same default order as in edit mode; General Day/Night are controlled by the general interval section, not here
  const defaultPositions = React.useMemo<IIndividualPosition[]>(
    () => [
      { name: 'Supine' },
      { name: 'Right Side' },
      { name: 'Left Side' },
      { name: 'Prone' },
      { name: 'Sitting' },
      { name: 'Wheelchair' },
    ],
    []
  );





  const timeOptions = React.useMemo(() => {
    const opts: string[] = [];
    for (let h = 0; h <= 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        if (h === 24 && m > 0) break;
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        opts.push(`${hh}:${mm}`);
      }
    }
    return opts;
  }, []);

  const intervalOptions = React.useMemo(() => {
    const options: { key: string; value: string }[] = [];
    // No Interval
    options.push({ key: 'noInterval', value: '00:00:00' });
    // 0.5h to 6h in 0.5h steps
    for (let half = 1; half <= 12; half++) {
      const hours = half * 0.5; // 0.5, 1.0, ... 6.0
      const hh = Math.floor(hours).toString().padStart(2, '0');
      const mm = hours % 1 === 0 ? '00' : '30';
      const value = `${hh}:${mm}:00`;
      const key = `every${hours}h`; // e.g., every0.5h, every1h, every1.5h, ... every6h
      options.push({ key, value });
    }
    return options;
  }, []);

  // Helper functions for time management

  // Time utility functions
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const addHoursToTime = (time: string, hours: number): string => {
    const minutes = timeToMinutes(time);
    const newMinutes = (minutes + hours * 60) % (24 * 60); // 24-hour cycle

    // Special case: if result is 00:00 (midnight), return 24:00 for UI display
    // This happens when we complete exactly 24 hours
    if (newMinutes === 0) {
      return '24:00';
    }

    return minutesToTime(newMinutes);
  };

  const calculateTimeDifference = (startTime: string, endTime: string): number => {
    let startMinutes = timeToMinutes(startTime);
    let endMinutes = timeToMinutes(endTime);

    if (endMinutes < startMinutes) {
      // If crossing midnight
      endMinutes += 24 * 60;
    }

    return endMinutes - startMinutes;
  };

  // Check if total time exceeds 24 hours
  const checkTotalTimeExceeds24Hours = (intervals: TimeInterval[]): boolean => {
    let totalMinutes = 0;
    for (const interval of intervals) {
      const duration = calculateTimeDifference(interval.start, interval.end);
      totalMinutes += duration;
    }
    return totalMinutes > 24 * 60;
  };

  const updateTimeInterval = (index: number, field: 'start' | 'end' | 'intervalValue', value: string) => {
    const newIntervals = [...timeIntervals];
    const currentInterval = newIntervals[index];

    console.log(`🔄 Updating interval ${index}, field: ${field}, value: ${value}`);

    // Validation: Basic time validations
    if (field === 'start' || field === 'end') {
      const startTime = field === 'start' ? value : currentInterval.start;
      const endTime = field === 'end' ? value : currentInterval.end;

      // Start and end times cannot be the same
      if (startTime === endTime) {
        console.error(`❌ Start and end times cannot be the same! (${startTime})`);
        toast({
          title: t('error'),
          description: `Start and end times cannot be the same! (${startTime})`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // Update the field
    newIntervals[index] = { ...currentInterval, [field]: value };

    // Special handling for first interval
    if (index === 0 && field === 'start') {
      // Special case: if start time is 00:00, set end time to 24:00 (single interval)
      // Otherwise, set end time to 12 hours later and create second interval
      const newEndTime = value === '00:00' ? '24:00' : addHoursToTime(value, 12);
      newIntervals[0].end = newEndTime;
      console.log(`✅ First interval start: ${value}, end auto: ${newEndTime}`);

      // Reset to single interval for 24-hour completion
      if (newIntervals.length > 1) {
        newIntervals.splice(1);
        console.log(`🗑️ Other intervals cleared when first interval changes`);
      }

      // Only create second interval if start time is NOT 00:00
      if (value !== '00:00') {
        // Now simulate the end time change to create the second interval automatically
        // This ensures 24-hour completion even when start time changes
        const simulatedValue = newEndTime;

        // First interval end changed simulation
        const firstStart = newIntervals[0].start;
        const firstEnd = simulatedValue;

        // Calculate how much of 24 hours is covered by first interval
        const firstDuration = calculateTimeDifference(firstStart, firstEnd);
        const remainingMinutes = (24 * 60) - firstDuration;

        console.log(`📊 First interval: ${firstStart} - ${firstEnd} (${firstDuration} minutes)`);
        console.log(`⏳ Remaining: ${remainingMinutes} minutes`);

        if (remainingMinutes > 0) {
          // Need additional intervals to complete 24 hours
          const nextStart = firstEnd;
          const nextEnd = addHoursToTime(nextStart, remainingMinutes / 60);

          if (newIntervals.length === 1) {
            // Create second interval
            const secondInterval = {
              start: nextStart,
              end: nextEnd,
              id: `auto-${Date.now()}`,
              isExisting: false,
              dbId: null,
              intervalValue: newIntervals[0].intervalValue
            };
            newIntervals.push(secondInterval);
            console.log(`➕ Second interval created: ${nextStart} - ${nextEnd}`);
          }
        }
      } else {
        console.log(`🎯 Start time is 00:00, keeping single interval: ${value} - ${newEndTime}`);
      }
    }

    // Handle end time changes
    if (field === 'end') {
      if (index === 0) {
        // First interval end changed
        const firstStart = newIntervals[0].start;
        const firstEnd = value;

        // Calculate how much of 24 hours is covered by first interval
        const firstDuration = calculateTimeDifference(firstStart, firstEnd);
        const remainingMinutes = (24 * 60) - firstDuration;

        console.log(`📊 First interval: ${firstStart} - ${firstEnd} (${firstDuration} minutes)`);
        console.log(`⏳ Remaining: ${remainingMinutes} minutes`);

        if (remainingMinutes > 0) {
          // Need additional intervals to complete 24 hours
          const nextStart = firstEnd;
          const nextEnd = addHoursToTime(nextStart, remainingMinutes / 60);

          if (newIntervals.length === 1) {
            // Create second interval
            const secondInterval = {
              start: nextStart,
              end: nextEnd,
              id: `auto-${Date.now()}`,
              isExisting: false,
              dbId: null,
              intervalValue: newIntervals[0].intervalValue
            };
            newIntervals.push(secondInterval);
            console.log(`➕ Second interval created: ${nextStart} - ${nextEnd}`);
          } else {
            // Update existing second interval
            newIntervals[1] = {
              ...newIntervals[1],
              start: nextStart,
              end: nextEnd,
              intervalValue: newIntervals[0].intervalValue
            };
            console.log(`✏️ Second interval updated: ${nextStart} - ${nextEnd}`);
          }
        } else if (remainingMinutes === 0) {
          // Exactly 24 hours completed, remove extra intervals
          if (newIntervals.length > 1) {
            newIntervals.splice(1);
            console.log(`✅ Exactly 24 hours completed, extra intervals removed`);
          }
        }
      } else {
        // Non-first interval end changed
        const currentEnd = value;
        const nextIntervalIndex = index + 1;

        // Calculate total time covered so far
        let totalMinutes = 0;
        for (let i = 0; i <= index; i++) {
          const interval = newIntervals[i];
          const duration = calculateTimeDifference(interval.start, interval.end);
          totalMinutes += duration;
        }

        const remainingMinutes = (24 * 60) - totalMinutes;
        console.log(`📊 Total covered: ${totalMinutes} minutes, remaining: ${remainingMinutes} minutes`);

        if (remainingMinutes > 0) {
          // Need next interval
          const nextStart = currentEnd;
          const nextEnd = addHoursToTime(nextStart, remainingMinutes / 60);

          if (nextIntervalIndex >= newIntervals.length) {
            // Create new interval
            const newInterval = {
              start: nextStart,
              end: nextEnd,
              id: `auto-${Date.now() + nextIntervalIndex}`,
              isExisting: false,
              dbId: null,
              intervalValue: newIntervals[0].intervalValue
            };
            newIntervals.push(newInterval);
            console.log(`➕ New interval created: ${nextStart} - ${nextEnd}`);
          } else {
            // Update existing next interval
            newIntervals[nextIntervalIndex] = {
              ...newIntervals[nextIntervalIndex],
              start: nextStart,
              end: nextEnd,
              intervalValue: newIntervals[0].intervalValue
            };
            console.log(`✏️ Next interval updated: ${nextStart} - ${nextEnd}`);
          }
        } else if (remainingMinutes === 0) {
          // Exactly 24 hours completed, remove extra intervals
          if (nextIntervalIndex < newIntervals.length) {
            newIntervals.splice(nextIntervalIndex);
            console.log(`✅ Exactly 24 hours completed, next intervals removed`);
          }
        }
      }
    }

    // Check if total time exceeds 24 hours before applying changes
    if (checkTotalTimeExceeds24Hours(newIntervals)) {
          console.error(`❌ Total time exceeds 24 hours!`);
      toast({
        title: t('error'),
        description: `Total duration cannot exceed 24 hours! Please readjust the intervals.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return; // Don't apply changes if total exceeds 24 hours
    }

    setTimeIntervals(newIntervals);
  };


  const fetchBedSores = async (residentId: string) => {
    try {
      const bedsores = await ResidentService.getResidentBedsores(residentId);
      const list: any[] = Array.isArray(bedsores)
        ? (bedsores as any[])
        : ((bedsores as any)?.items || []);
      if (list && Array.isArray(list)) {
        const reverseMap: Record<string, string> = Object.entries(bodyRegions as any).reduce((acc, [key, val]) => {
          acc[val as string] = key;
          return acc;
        }, {} as Record<string, string>);

        const newSelections = list.reduce(
          (acc: BedSoreSelections, bedsore: any) => {
            const idKey = reverseMap[bedsore.region] || bedsore.region;
            acc[idKey] = {
              risk: bedsore.risk,
              note: bedsore.note,
            };
            return acc;
          },
          {}
        );
        setSelections(newSelections);
        setValue('bedsoreOps', []); // reset queued ops on fresh load
      }
    } catch (error) {
      console.log(error);
    }
  };


  useEffect(() => {
    if (residentId) {
      console.log('🔄 useEffect triggered with residentId:', residentId);
      fetchBedSores(residentId);
    }
  }, [residentId]);

  // Debug effect to monitor timeIntervals state changes
  useEffect(() => {
    console.log('📊 timeIntervals state changed:', timeIntervals);
  }, [timeIntervals]);

  // Submit-only approach: no immediate API call on body interactions

  // Normalize gender for create flow where it might be temporarily undefined
  const normalizeGender = (g: any): 'male' | 'female' | 'diverse' => {
    const val = (g?.toString?.() || 'female').toLowerCase();
    if (val === 'man') return 'male';
    if (val === 'woman') return 'female';
    if (val === 'diverse') return 'female';
    if (val === 'male' || val === 'female') return val;
    return 'female';
  };

  gender = normalizeGender(gender);

  return (
    <Flex direction="column" alignItems="center">
      {/* Section Title */}
      <Box w="100%" maxWidth="949px" mb="50px">
        <ResidentHeadline
          title={t('repositioning')}
        />
      </Box>
      {/* Section 1 */}
      <Box mb="50px" w="100%" maxWidth="949px">
        <Box>
          <Flex justifyContent="space-between">
            <Flex direction="column">
              <Text color="#000" fontSize="20px" fontWeight={500}>
                {t('provideReposIntervalHeadline')}
              </Text>
              <Text color="#000" fontSize="15px" fontWeight={300}>
                {t('provideReposIntervalSubline')}
              </Text>
            </Flex>
            <Flex minWidth="150px" alignItems="center">
              <AleaToggleInline
                isChecked={enableReposIntervals}
                onToggle={(e) => {
                  setValue('enableReposIntervals', e.target.checked);
                }}
                leftLabel={t('no')}
                rightLabel={t('yes')}
              />
            </Flex>
          </Flex>

          {enableReposIntervals && (
            <Box pl="25px" borderRadius="md" mt="50px">
              {/* Sub child 1 */}
              <Flex justifyContent="space-between" mb="25px" maxWidth="537px">
                <Flex direction="column">
                  <Text color="#000" fontSize="20px" fontWeight={500}>
                    {t('defineReposIntervalHeadline')}
                  </Text>
                  <Text color="#000" fontSize="15px" fontWeight={300}>
                    {t('defineReposIntervalSubline')}
                  </Text>
                </Flex>
              </Flex>

              {/* Dynamic Time Intervals for 24-hour completion */}
              <Box mb="20px">
                <Text fontSize="16px" fontWeight="500" color="#000" mb="15px">
                  {t('timeIntervals24HourRule')}
                </Text>

                {timeIntervals.map((interval, index) => (
                  <Flex key={interval.id} gap="24px" alignItems="center" mb={index === timeIntervals.length - 1 ? "15px" : "8px"} maxWidth="700px" mx="auto">
                    {/* Time Interval (von / bis) */}
                    <Box minW="300px" flex="1">
                      <FormControl>
                        {index === 0 && (
                          <FormLabel fontSize="14px" fontWeight={300} color="#000" pl="16px" mb="8px">
                            {t('timeInterval')}
                          </FormLabel>
                        )}
                        <Flex alignItems="center">
                          <Select
                            value={interval.start}
                            onChange={(e) => updateTimeInterval(index, 'start', e.target.value)}
                            borderRadius="20px"
                            borderColor="#DCDCDC"
                            h="50px"
                            color="black"
                            bg="white"
                            maxW="120px"
                            disabled={index > 0} // Only first interval is editable
                          >
                            {timeOptions.map((opt) => (
                              <option key={`start-${index}-${opt}`} value={opt}>{opt}</option>
                            ))}
                          </Select>
                          <Text mx="8px">-</Text>
                          <Select
                            value={interval.end}
                            onChange={(e) => updateTimeInterval(index, 'end', e.target.value)}
                            borderRadius="20px"
                            borderColor="#DCDCDC"
                            h="50px"
                            color="black"
                            bg="white"
                            maxW="120px"
                          >
                            {timeOptions.map((opt) => (
                              <option key={`end-${index}-${opt}`} value={opt}>{opt}</option>
                            ))}
                          </Select>
                        </Flex>
                      </FormControl>
                    </Box>

                    {/* Interval Duration - Individual for each interval */}
                    <Box minW="280px" flex="1">
                      <FormControl>
                        {index === 0 && (
                          <FormLabel fontSize="14px" fontWeight={300} color="#000" pl="16px" mb="8px">
                            {t('reposInterval')}
                          </FormLabel>
                        )}
                        <Select
                          value={interval.intervalValue || '00:00:00'}
                          onChange={(e) => {
                            updateTimeInterval(index, 'intervalValue', e.target.value);
                          }}
                          borderRadius="20px"
                          borderColor="#DCDCDC"
                          h="50px"
                          color="black"
                          bg="white"
                        >
                          {intervalOptions.map((opt) => (
                            <option key={`interval-${index}-${opt.key}`} value={opt.value}>
                              {opt.key === 'noInterval' ? t('noReposInterval') : t(opt.key, { keySeparator: false })}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Flex>
                ))}
              </Box>


              {/* Sub child 2 */}
              <Flex justifyContent="space-between" mt="50px">
                <Flex direction="column">
                  <Text color="#000" fontSize="20px" fontWeight={500}>
                    {t('defineReposIntervalBodyPartsHeadline')}
                  </Text>
                  <Text color="#000" fontSize="15px" fontWeight={300}>
                    {t('defineReposIntervalBodyPartsSubline')}
                  </Text>
                </Flex>
                <Flex minWidth="150px" alignItems="center">
                  <AleaToggleInline
                    isChecked={enableReposPositionIntervals}
                    onToggle={(e) => {
                      setValue('enableReposPositionIntervals', e.target.checked);
                    }}
                    leftLabel={t('no')}
                    rightLabel={t('yes')}
                  />
                </Flex>
              </Flex>
              {/*AleaIndividualPositions*/}
              {enableReposPositionIntervals && (
                <Box maxWidth="800px" w="100%" mt="20px">
                  <Flex flexFlow="column">
                    <AleaIndividualPositions
                      positions={positions.length ? positions : defaultPositions}
                      setPositions={(newPositions: IIndividualPosition[]) => {
                        setValue('positions', newPositions);
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Section 2 */}
      <Box mb="40px" w="100%" maxWidth="949px">
        <Box maxWidth="949px">
          <Flex justifyContent="space-between">
            <Flex direction="column">
              <Text color="#000" fontSize="20px" fontWeight={500}>
                {t('markEndangeredAffectedBodyPartsHeadline')}
              </Text>
              <Text color="#000" fontSize="15px" fontWeight={300}>
                {t('markEndangeredAffectedBodyPartsSubline')}
              </Text>
            </Flex>
            <Flex minWidth="150px" alignItems="center">
              <AleaToggleInline
                isChecked={enableReposBedsores}
                onToggle={(e) => {
                  setValue('enableReposBedsores', e.target.checked);
                }}
                leftLabel={t('no')}
                rightLabel={t('yes')}
              />
            </Flex>
          </Flex>

          {enableReposBedsores && (
            <Box mt="20px" p="20px" borderRadius="md">
              <Box mb="20px">
                <Box mb="25px">
                  <Center>
                    <Flex>
                      <Flex padding="30px">
                        <BedsoreSelection
                          title={t('bedsoreFront')}
                          gender={gender}
                          view="front"
                          tooltipPlacement="left"
                          existingSelections={selections}
                          updateSelections={(updatedSelections) => {
                            // merge with latest form value to avoid overwriting from other view
                            const current = (watch('selections') || {}) as BedSoreSelections;
                            const merged = { ...current, ...updatedSelections } as BedSoreSelections;
                            setValue('selections', merged);
                            setSelections(merged);
                            // queue ops
                            const ops: BedsoresPreventionFormValues['bedsoreOps'] = [];
                            Object.entries(updatedSelections).forEach(([key, item]) => {
                              const existed = !!current[key];
                              ops.push({ action: existed ? 'update' : 'create', key, risk: item.risk, note: item.note });
                            });
                            setValue('bedsoreOps', [ ...(watch('bedsoreOps')||[]), ...ops ]);
                          }}
                          onUpdate={undefined}
                          onDelete={(key) => {
                            const current = (watch('selections') || {}) as BedSoreSelections;
                            const newSelections = { ...current } as any;
                            delete newSelections[key];
                            setValue('selections', newSelections);
                            setSelections(newSelections);
                            setValue('bedsoreOps', [ ...(watch('bedsoreOps')||[]), { action: 'delete', key } ]);
                          }}
                          onCreate={undefined}
                        />
                      </Flex>
                      <Flex padding="30px">
                        <BedsoreSelection
                          title={t('bedsoreBack')}
                          gender={gender}
                          view="back"
                          tooltipPlacement="right"
                          existingSelections={selections}
                          updateSelections={(updatedSelections) => {
                            const current = (watch('selections') || {}) as BedSoreSelections;
                            const merged = { ...current, ...updatedSelections } as BedSoreSelections;
                            setValue('selections', merged);
                            setSelections(merged);
                            const ops: BedsoresPreventionFormValues['bedsoreOps'] = [];
                            Object.entries(updatedSelections).forEach(([key, item]) => {
                              const existed = !!current[key];
                              ops.push({ action: existed ? 'update' : 'create', key, risk: item.risk, note: item.note });
                            });
                            setValue('bedsoreOps', [ ...(watch('bedsoreOps')||[]), ...ops ]);
                          }}
                          onUpdate={undefined}
                          onDelete={(key) => {
                            const current = (watch('selections') || {}) as BedSoreSelections;
                            const newSelections = { ...current } as any;
                            delete newSelections[key];
                            setValue('selections', newSelections);
                            setSelections(newSelections);
                            setValue('bedsoreOps', [ ...(watch('bedsoreOps')||[]), { action: 'delete', key } ]);
                          }}
                          onCreate={undefined}
                        />
                      </Flex>
                    </Flex>
                  </Center>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Flex>
  );
}

export default BedsoresPreventionTab;
